import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AddIcon } from '@/components/icons';
import {
  App,
  AutoComplete,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  Typography,
} from 'antd';
import type { Rule } from 'antd/es/form';
import type { InputRef } from 'antd';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { createDraftMedicine, listBrands, listManufacturers } from '@/api/catalog.api';
import { PickerPopup } from '@/components/PickerPopup';
import type { DraftMedicineInput, Medicine, Uuid } from '@/api/types';
import {
  BASE_UNITS,
  PACK_FORMS,
  packNoun,
  unitNoun,
  type BaseUnit,
  type PackForm,
} from '@/lib/format';

const { Text } = Typography;

/*
 * The same lists the admin panel's medicine form uses, so a draft keyed here
 * and a product created there describe a pack in the same words. Pack form and
 * base unit come from `format`, which is where every other screen gets them;
 * these two have no shared home yet and are copied verbatim from the admin.
 */
/*
 * Product type, in the words and the order of the billing software counters
 * already use: the schedule and the kind of goods in one list, because that is
 * how a pharmacist reads a carton - "this is H1", "this is a soap". Behind it
 * are two separate facts, the catalogue's product type and the schedule, and
 * each choice says which of both it means.
 */
type Kind =
  | 'GENERAL'
  | 'SCHEDULE_H1'
  | 'SCHEDULE_H'
  | 'NARCOTICS'
  | 'CODEINE'
  | 'FOOD'
  | 'COSMETICS'
  | 'SUPPLEMENT'
  | 'DEVICE'
  | 'CONSUMABLE';

type KindSpec = {
  label: string;
  /** Said under the field once chosen: what the choice does at the counter. */
  note: string;
  productType: NonNullable<DraftMedicineInput['productType']>;
  schedule?: DraftMedicineInput['schedule'];
  /** A glucometer, a box of gloves or a health drink has no strength or salts to ask about. */
  hasDose: boolean;
};

const KINDS: Record<Kind, KindSpec> = {
  GENERAL: {
    label: 'General',
    note: 'A medicine sold over the counter, with no prescription.',
    productType: 'MEDICINE',
    schedule: 'GENERAL',
    hasDose: true,
  },
  SCHEDULE_H1: {
    label: 'Schedule H1',
    note: 'Prescription only, and every sale goes in the H1 register.',
    productType: 'MEDICINE',
    schedule: 'H1',
    hasDose: true,
  },
  SCHEDULE_H: {
    label: 'Schedule H',
    note: 'Prescription only. Most Rx medicines are this.',
    productType: 'MEDICINE',
    schedule: 'H',
    hasDose: true,
  },
  NARCOTICS: {
    label: 'Narcotics',
    note: 'Schedule X or NDPS. Prescription kept on file, stock kept locked.',
    productType: 'MEDICINE',
    schedule: 'NARCOTIC',
    hasDose: true,
  },
  CODEINE: {
    label: 'Codeine',
    note: 'Codeine combinations. Treated as H1 and as a controlled medicine.',
    productType: 'MEDICINE',
    schedule: 'CODEINE',
    hasDose: true,
  },
  FOOD: {
    label: 'Food items',
    note: 'Health drinks, infant food, sweeteners. Not a drug, so no prescription rules.',
    productType: 'FOOD',
    hasDose: false,
  },
  COSMETICS: {
    label: 'Medicated soaps / cosmetics',
    note: 'Soaps, creams, shampoos and lotions. No prescription rules.',
    productType: 'PERSONAL_CARE',
    hasDose: true,
  },
  SUPPLEMENT: {
    label: 'Supplement',
    note: 'Vitamins and nutraceuticals. No prescription rules.',
    productType: 'SUPPLEMENT',
    hasDose: true,
  },
  DEVICE: {
    label: 'Device',
    note: 'Glucometers, BP monitors, thermometers.',
    productType: 'DEVICE',
    hasDose: false,
  },
  CONSUMABLE: {
    label: 'Consumable',
    note: 'Gloves, syringes, bandages, strips.',
    productType: 'CONSUMABLE',
    hasDose: false,
  },
};

const KIND_OPTIONS = [
  {
    label: 'Medicines',
    options: (['GENERAL', 'SCHEDULE_H1', 'SCHEDULE_H', 'NARCOTICS', 'CODEINE'] as const).map(
      (value) => ({ value, label: KINDS[value].label }),
    ),
  },
  {
    label: 'Other products',
    options: (['FOOD', 'COSMETICS', 'SUPPLEMENT', 'DEVICE', 'CONSUMABLE'] as const).map(
      (value) => ({ value, label: KINDS[value].label }),
    ),
  },
];

const DOSAGE_FORMS = [
  'Tablet',
  'Capsule',
  'Syrup',
  'Suspension',
  'Injection',
  'Cream',
  'Ointment',
  'Drops',
  'Inhaler',
  'Gel',
  'Sachet',
];

const titled = (value: string) => value[0] + value.slice(1).toLowerCase().replace('_', ' ');

/** What a blank form holds. */
const DEFAULTS = {
  // The usual Rx line, and the safe default: prescription only until said otherwise.
  kind: 'SCHEDULE_H',
  packForm: 'STRIP',
  baseUnit: 'TABLET',
  allowLooseSale: false,
  hsnCode: '30049099',
  gstRate: '5',
} satisfies Partial<FormValues>;

/** An existing row by id, or a new one by name. Never both. */
type Choice = { id?: Uuid; name?: string };

type FormValues = {
  name: string;
  kind: Kind;
  barcode?: string;
  dosageForm?: string;
  strength?: string;
  packForm?: PackForm;
  baseUnit?: BaseUnit;
  unitsPerPack?: number;
  packsPerBox?: number;
  allowLooseSale?: boolean;
  hsnCode?: string;
  gstRate?: string;
  mrp?: number;
  composition?: string;
};

/**
 * One field as a field card: the label on the tray, the box on the white card,
 * and a note or the rule's message under it. The same piece the supplier form
 * and the till use.
 */
function Field({
  name,
  label,
  note,
  rules,
  valuePropName,
  className,
  children,
}: {
  name: keyof FormValues;
  label: string;
  note?: ReactNode;
  rules?: Rule[];
  valuePropName?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`field-card ${className ?? ''}`}>
      <label className="field-card-label" htmlFor={`product_${name}`}>
        {label}
      </label>
      <Form.Item
        name={name}
        rules={rules}
        extra={note}
        valuePropName={valuePropName}
        className="form-in-card"
      >
        {children}
      </Form.Item>
    </div>
  );
}

/**
 * A select that can also add.
 *
 * The register of makers and brands is never complete - a new wholesaler
 * brings a new maker every month - and sending the clerk to an admin to add
 * one before they can book a delivery is how a delivery gets keyed against the
 * wrong product instead. So the picker always offers to add what was typed, and
 * the add is just a name: the server creates the row with the draft.
 */
function PickOrAdd({
  label,
  noun,
  term,
  onSearch,
  options,
  loading,
  value,
  onChange,
}: {
  label: string;
  noun: string;
  term: string;
  onSearch: (term: string) => void;
  options: { value: Uuid; label: string }[];
  loading: boolean;
  value: Choice;
  onChange: (next: Choice) => void;
}) {
  const cleaned = term.trim();
  return (
    <div className="field-card">
      <span className="field-card-label">{label}</span>
      {value.name ? (
        <div className="product-new-pick">
          <Tag className="m-0!">New</Tag>
          <span className="min-w-0 flex-1 truncate">{value.name}</span>
          <Button size="small" type="text" onClick={() => onChange({})}>
            Change
          </Button>
        </div>
      ) : (
        <Select
          showSearch
          allowClear
          filterOption={false}
          onSearch={onSearch}
          loading={loading}
          placeholder={`Search, or type a new ${noun}`}
          value={value.id}
          onChange={(id) => onChange(id ? { id } : {})}
          options={options}
          aria-label={label}
          notFoundContent={
            <Text type="secondary" className="block px-1 py-1 text-xs">
              {cleaned ? `No ${noun} called "${cleaned}" yet.` : `Type to search ${noun}s`}
            </Text>
          }
          popupRender={(menu) => (
            <PickerPopup
              label={`${noun[0]!.toUpperCase()}${noun.slice(1)}s`}
              count={options.length > 0 ? options.length : undefined}
              menu={menu}
              footer={
                cleaned ? (
                  <Button
                    type="text"
                    size="small"
                    icon={<AddIcon />}
                    className="picker-action picker-action-solo"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onChange({ name: cleaned })}
                  >
                    Add "{cleaned}" as a new {noun}
                  </Button>
                ) : null
              }
            />
          )}
        />
      )}
    </div>
  );
}

/**
 * "Strip of 15 tablets", from the three answers about the pack.
 *
 * The catalogue stores the pack size as a sentence, because every other screen
 * prints it. It is built from the answers so it cannot disagree with them.
 */
function describe(form?: PackForm, unit?: BaseUnit, count?: number): string | undefined {
  if (!form || !count) return undefined;
  /* `packNoun` is already a word ("strip"), not an enum: capitalised here, or
     the stored pack size read "strip of 15 tablets" on every screen. */
  const noun = packNoun(form, 1);
  const pack = noun.charAt(0).toUpperCase() + noun.slice(1);
  return unit ? `${pack} of ${count} ${unitNoun(unit, count)}` : `${pack} of ${count}`;
}

/**
 * Add a product the catalogue does not have, from the pack in hand.
 *
 * It lands as a DRAFT: usable here the moment it is saved, visible to this
 * pharmacy alone, and queued for the platform to check and publish.
 *
 * Four sections, in the order the carton is read: what it is and who makes
 * it, what is in it, how it is packed, and the tax and price. The questions
 * are the admin panel's, in the same words, because those are what every
 * later screen counts in: a till selling loose tablets divides by "how many",
 * and a box line multiplies by "packs in a box".
 *
 * `purpose` says where it was opened from. On a purchase line the new product
 * goes straight onto that line; opened on its own (the shortcut, the Today
 * page) it is simply added, ready to book in.
 */
export function NewProductDrawer({
  open,
  pharmacyId,
  initialName,
  purpose = 'line',
  onClose,
  onCreated,
}: {
  open: boolean;
  pharmacyId: Uuid;
  initialName: string;
  purpose?: 'line' | 'catalogue';
  onClose: () => void;
  onCreated: (medicine: Medicine) => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  const [maker, setMaker] = useState<Choice>({});
  const [makerTerm, setMakerTerm] = useState('');
  const [brand, setBrand] = useState<Choice>({});
  const [brandTerm, setBrandTerm] = useState('');
  /* Products added since this drawer opened, when it stays open for the next. */
  const [added, setAdded] = useState(0);
  const nameRef = useRef<InputRef>(null);

  /*
   * Back to a blank form: the fields, and the maker and brand picks, which
   * live outside the form. The drawer stays mounted between uses, so without
   * this the last product's answers were still filled in next time - and a
   * strength or a manufacturer carried over unnoticed is a wrong catalogue row.
   */
  function reset(name: string) {
    form.resetFields();
    form.setFieldsValue({ ...DEFAULTS, name });
    setMaker({});
    setMakerTerm('');
    setBrand({});
    setBrandTerm('');
  }

  /* Every open starts blank, with whatever the search box held as the name. */
  useEffect(() => {
    if (!open) return;
    reset(initialName);
    setAdded(0);
  }, [open, initialName]);

  const kind = Form.useWatch('kind', form);
  const packForm = Form.useWatch('packForm', form);
  const baseUnit = Form.useWatch('baseUnit', form);
  const unitsPerPack = Form.useWatch('unitsPerPack', form);
  const packsPerBox = Form.useWatch('packsPerBox', form);
  const allowLooseSale = Form.useWatch('allowLooseSale', form);
  const derivedPackSize = describe(packForm, baseUnit, unitsPerPack);
  const hasDose = !kind || KINDS[kind].hasDose;

  /* The register of makers is a few hundred rows: fetched once, filtered here. */
  const makers = useQuery({
    queryKey: ['manufacturers', 'picker'],
    queryFn: () => listManufacturers({ limit: 100 }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const makerOptions = useMemo(() => {
    const needle = makerTerm.trim().toLowerCase();
    return (makers.data?.data ?? [])
      .filter((m) => !needle || m.name.toLowerCase().includes(needle))
      .slice(0, 20)
      .map((m) => ({ value: m.id, label: m.name }));
  }, [makers.data, makerTerm]);

  const brands = useQuery({
    queryKey: ['brands', 'picker', brandTerm.trim(), maker.id ?? null],
    queryFn: () =>
      listBrands({ q: brandTerm.trim() || undefined, manufacturerId: maker.id, limit: 20 }),
    enabled: open,
    staleTime: 60_000,
  });
  const brandOptions = (brands.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }));

  const create = useMutation({
    mutationFn: (body: DraftMedicineInput) => createDraftMedicine(pharmacyId, body),
    onSuccess: async (medicine) => {
      /* Every open picker is searching the same catalogue; the new draft has to
         appear in all of them, not only the one that made it. */
      await queryClient.invalidateQueries({ queryKey: ['medicines'] });
      if (purpose === 'line') {
        message.success(
          `${medicine.name} added and put on this line. The catalogue team checks it before it is published.`,
        );
        onCreated(medicine);
        return;
      }
      /*
       * Opened on its own, products usually come in a run - a new wholesaler's
       * invoice, a shelf of new lines. So the drawer stays open, cleared, with
       * the cursor back in Name for the next one. Done closes it.
       */
      message.success(`${medicine.name} added. The form is clear for the next product.`);
      reset('');
      setAdded((n) => n + 1);
      requestAnimationFrame(() => nameRef.current?.focus());
      onCreated(medicine);
    },
    onError: (error: Error) => message.error(error.message),
  });

  async function submit() {
    const v = await form.validateFields();
    const spec = KINDS[v.kind];
    const dose = spec.hasDose;
    const body: DraftMedicineInput = {
      name: v.name.trim(),
      productType: spec.productType,
      schedule: spec.schedule,
      barcode: v.barcode?.trim() || undefined,
      dosageForm: dose ? v.dosageForm?.trim() || undefined : undefined,
      strength: dose ? v.strength?.trim() || undefined : undefined,
      composition: dose ? v.composition?.trim() || undefined : undefined,
      packSize: derivedPackSize,
      packForm: v.packForm,
      baseUnit: v.baseUnit,
      unitsPerPack: v.unitsPerPack ?? undefined,
      packsPerBox: v.packsPerBox ?? undefined,
      allowLooseSale: v.allowLooseSale ?? false,
      hsnCode: v.hsnCode?.trim() || undefined,
      gstRate: v.gstRate || undefined,
      typicalMrp: v.mrp != null ? v.mrp.toFixed(2) : undefined,
      ...(maker.id
        ? { manufacturerId: maker.id }
        : maker.name
          ? { newManufacturer: { name: maker.name } }
          : {}),
      ...(brand.id ? { brandId: brand.id } : brand.name ? { newBrand: { name: brand.name } } : {}),
    };
    create.mutate(body);
  }

  /* The pack, said as one sentence, so "how many" and "packs in a box" - two
     counts that read alike - cannot be confused. */
  const packSentence = derivedPackSize
    ? `One ${packNoun(packForm, 1)} holds ${unitsPerPack} ${unitNoun(baseUnit, unitsPerPack)}${
        packsPerBox ? `, and a box holds ${packsPerBox} ${packNoun(packForm, packsPerBox)}` : ''
      }.${allowLooseSale ? ` It can also be sold by the ${unitNoun(baseUnit, 1)}.` : ''}`
    : 'Say what a pack is and how many it holds.';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      /* Kept mounted so the form can be reset in place; see `reset`. */
      afterOpenChange={(visible) => {
        if (visible) nameRef.current?.focus();
      }}
      width={600}
      title={
        added > 0 ? (
          <span className="flex items-baseline gap-2">
            Add a new product
            <Text type="secondary" className="text-xs font-normal" data-numeric>
              {added} added
            </Text>
          </span>
        ) : (
          'Add a new product'
        )
      }
      extra={
        <div className="flex gap-2">
          <Button onClick={onClose}>{added > 0 ? 'Done' : 'Cancel'}</Button>
          <Button type="primary" loading={create.isPending} onClick={() => void submit()}>
            {purpose === 'line' ? 'Add to invoice' : 'Add product'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        name="product"
        layout="vertical"
        requiredMark={false}
        initialValues={{ ...DEFAULTS, name: initialName }}
        onFinish={() => void submit()}
        className="flex flex-col gap-3"
      >
        {/* Said once, before anything is typed: what saving this does. */}
        <p className="product-intro">
          Not in the catalogue yet? Add it from the pack in hand. It is saved as a draft for
          your shop, usable straight away, and published for everyone once checked.
        </p>

        <Card
          size="small"
          className="card-fields"
          title={<h3 className="card-heading">What it is</h3>}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Field
              name="name"
              label="Name"
              className="sm:col-span-2"
              rules={[
                { required: true, whitespace: true, message: 'Name is required' },
                { min: 2, message: 'Use at least two characters' },
              ]}
            >
              <Input ref={nameRef} id="product_name" placeholder="Dolo 650 Tablet" />
            </Field>
            <Field name="kind" label="Product type" note={kind ? KINDS[kind].note : undefined}>
              <Select id="product_kind" options={KIND_OPTIONS} listHeight={320} />
            </Field>
            <Field name="barcode" label="Barcode" note="Scan the pack, or leave it blank.">
              <Input id="product_barcode" placeholder="8901234567890" inputMode="numeric" />
            </Field>
            {/* Manufacturer before brand: the brand list narrows to the maker picked. */}
            <PickOrAdd
              label="Manufacturer"
              noun="manufacturer"
              term={makerTerm}
              onSearch={setMakerTerm}
              options={makerOptions}
              loading={makers.isPending}
              value={maker}
              onChange={(next) => {
                setMaker(next);
                /* A brand belongs to a maker; changing the maker invalidates the pick. */
                setBrand({});
              }}
            />
            <PickOrAdd
              label="Brand"
              noun="brand"
              term={brandTerm}
              onSearch={setBrandTerm}
              options={brandOptions}
              loading={brands.isFetching}
              value={brand}
              onChange={setBrand}
            />
          </div>
        </Card>

        {hasDose ? (
          <Card
            size="small"
            className="card-fields"
            title={<h3 className="card-heading">What is in it</h3>}
          >
            <div className="grid gap-2 sm:grid-cols-2">
              <Field name="dosageForm" label="Dosage form">
                <AutoComplete
                  id="product_dosageForm"
                  placeholder="Tablet"
                  options={DOSAGE_FORMS.map((value) => ({ value }))}
                  filterOption={(input, option) =>
                    String(option?.value ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Field>
              <Field name="strength" label="Strength">
                <Input id="product_strength" placeholder="650 mg" />
              </Field>
              <Field
                name="composition"
                label="Composition"
                className="sm:col-span-2"
                note="The salts, as printed on the pack. The catalogue team checks it."
              >
                <Input.TextArea
                  id="product_composition"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  placeholder="Paracetamol 650 mg"
                  maxLength={500}
                />
              </Field>
            </div>
          </Card>
        ) : null}

        <Card
          size="small"
          className="card-fields"
          title={<h3 className="card-heading">How it is packed</h3>}
        >
          <div className="grid gap-2 sm:grid-cols-3">
            <Field name="packForm" label="A pack is a">
              <Select
                id="product_packForm"
                placeholder="Strip"
                options={PACK_FORMS.map((value) => ({ value, label: titled(value) }))}
              />
            </Field>
            <Field name="baseUnit" label="Of">
              <Select
                id="product_baseUnit"
                placeholder="Tablet"
                options={BASE_UNITS.map((value) => ({ value, label: titled(value) }))}
              />
            </Field>
            <Field name="unitsPerPack" label="How many">
              <InputNumber
                id="product_unitsPerPack"
                className="w-full"
                min={1}
                max={10000}
                placeholder="15"
              />
            </Field>
            <Field name="packsPerBox" label="Packs in a box" note="As the wholesaler sends it.">
              <InputNumber
                id="product_packsPerBox"
                className="w-full"
                min={1}
                max={1000}
                placeholder="10"
              />
            </Field>
            <Field
              name="allowLooseSale"
              label="Sell loose"
              valuePropName="checked"
              className="sm:col-span-2"
              note={`Lets the counter sell ${unitNoun(baseUnit, 2)} out of an open ${packNoun(packForm, 1)}.`}
            >
              <Switch id="product_allowLooseSale" />
            </Field>
          </div>
          <p className="product-pack-sentence" aria-live="polite">
            {packSentence}
          </p>
        </Card>

        <Card
          size="small"
          className="card-fields"
          title={<h3 className="card-heading">Tax and price</h3>}
        >
          <div className="grid gap-2 sm:grid-cols-3">
            <Field name="hsnCode" label="HSN code">
              <Input id="product_hsnCode" placeholder="30049099" maxLength={16} />
            </Field>
            <Field name="gstRate" label="GST">
              <Select
                id="product_gstRate"
                options={['0', '5', '12', '18'].map((rate) => ({ value: rate, label: `${rate}%` }))}
              />
            </Field>
            <Field name="mrp" label="Typical MRP" note="Per pack. Each batch keeps its own.">
              <InputNumber
                id="product_mrp"
                className="w-full"
                min={0}
                step={0.5}
                prefix="₹"
                placeholder="34.50"
              />
            </Field>
          </div>
        </Card>
      </Form>
    </Drawer>
  );
}
